import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Building2, CreditCard, Palette, CheckCircle, Clock, AlertCircle, Send,
  Eye, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';

const defaultBranding = {
  primary_color: '#3b82f6',
  secondary_color: '#1e293b',
  accent_color: '#10b981',
  sidebar_color: '#ffffff',
  logo_url: '',
  favicon_url: '',
  font: 'Inter',
  allow_downline_branding: false,
};

export default function TenantSettings() {
  const { user, loadUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({});
  const [branding, setBranding] = useState(defaultBranding);
  const [saving, setSaving] = useState(false);
  const [merchantAccount, setMerchantAccount] = useState(null);
  const [loadingMerchant, setLoadingMerchant] = useState(true);
  const maverickRef = useRef(null);
  const maverickInitialized = useRef(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isISO = user?.role === 'iso' || isSuperAdmin;

  const loadAll = useCallback(async () => {
    try {
      const data = await api.getTenantSettingsAll();
      if (data?.profile) {
        setProfile(data.profile);
        setBranding({
          primary_color: data.profile.primary_color || defaultBranding.primary_color,
          secondary_color: data.profile.secondary_color || defaultBranding.secondary_color,
          accent_color: data.profile.accent_color || defaultBranding.accent_color,
          sidebar_color: data.profile.sidebar_color || defaultBranding.sidebar_color,
          logo_url: data.profile.logo_url || '',
          favicon_url: data.profile.favicon_url || '',
          font: data.profile.font || defaultBranding.font,
          allow_downline_branding: data.profile.allow_downline_branding || false,
        });
      }
    } catch (err) {
      console.error('[TenantSettings] Failed to load branding profile:', err);
    }
    try {
      const ma = await api.getMyMerchantAccount();
      setMerchantAccount(ma);
    } catch {
      setMerchantAccount({ status: 'not_applied' });
    } finally {
      setLoadingMerchant(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.updateTenantSettings('profile', profile);
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await api.updateTenantBranding(branding);
      toast.success('Branding saved');
      // Reload user to apply branding changes across the app
      loadUser();
    } catch (err) {
      toast.error(err.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const resetBranding = () => {
    setBranding(defaultBranding);
  };

  // Maverick boarding script loader
  const initMaverick = useCallback(() => {
    if (maverickInitialized.current) return;
    const container = maverickRef.current;
    if (!container) return;
    container.innerHTML = '<div id="maverick-boarding"></div>';
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://merchantsolutionscorpdb.com/js/campaign/client.js?v=' + Date.now();
    script.setAttribute('data-maverick-client', 'true');
    script.onload = () => {
      if (window.MaverickClient) {
        new window.MaverickClient({
          target: 'maverick-boarding',
          url: 'https://merchantsolutionscorpdb.com/js/campaign/client.js',
          options: { id: '1258', agentId: '132194', referral: '1', title: '', theme: 'light', label: 'true' }
        });
        maverickInitialized.current = true;
      }
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (activeTab === 'payments' && !maverickInitialized.current) {
      const timer = setTimeout(initMaverick, 200);
      return () => clearTimeout(timer);
    }
  }, [activeTab, initMaverick]);

  const handleApply = async () => {
    try {
      await api.applyMerchantAccount();
      toast.success('Application submitted!');
      const ma = await api.getMyMerchantAccount();
      setMerchantAccount(ma);
    } catch (err) {
      toast.error(err.message || 'Application failed');
    }
  };

  const statusIcon = (s) => {
    if (s === 'active') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (s === 'pending') return <Clock className="h-5 w-5 text-amber-500" />;
    if (s === 'rejected') return <AlertCircle className="h-5 w-5 text-red-500" />;
    return <CreditCard className="h-5 w-5 text-muted-foreground" />;
  };

  const statusLabel = { not_applied: 'Not Applied', pending: 'Pending Review', approved: 'Approved', active: 'Active', rejected: 'Rejected' };

  const setBrandField = (k, v) => setBranding((b) => ({ ...b, [k]: v }));
  const setProfileField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="tenant-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your organization, branding, and payment account</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">
            <Building2 className="h-4 w-4 mr-2" /> Organization
          </TabsTrigger>
          {isISO && (
            <TabsTrigger value="branding" data-testid="settings-tab-branding">
              <Palette className="h-4 w-4 mr-2" /> Branding
            </TabsTrigger>
          )}
          <TabsTrigger value="payments" data-testid="settings-tab-payments">
            <CreditCard className="h-4 w-4 mr-2" /> Payment Account
          </TabsTrigger>
        </TabsList>

        {/* ── Organization Tab ──────────────────── */}
        <TabsContent value="profile">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Organization Profile</h3>
            <div className="grid grid-cols-2 gap-4 max-w-2xl">
              <div><Label>Company Name</Label><Input value={profile.company_name || ''} onChange={(e) => setProfileField('company_name', e.target.value)} className="mt-1" /></div>
              <div><Label>DBA Name</Label><Input value={profile.dba_name || ''} onChange={(e) => setProfileField('dba_name', e.target.value)} className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={profile.phone || ''} onChange={(e) => setProfileField('phone', e.target.value)} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={profile.email || ''} onChange={(e) => setProfileField('email', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>Website</Label><Input value={profile.website || ''} onChange={(e) => setProfileField('website', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={profile.address_line_1 || ''} onChange={(e) => setProfileField('address_line_1', e.target.value)} className="mt-1" /></div>
              <div><Label>City</Label><Input value={profile.city || ''} onChange={(e) => setProfileField('city', e.target.value)} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>State</Label><Input value={profile.state || ''} onChange={(e) => setProfileField('state', e.target.value)} className="mt-1" /></div>
                <div><Label>ZIP</Label><Input value={profile.zip || ''} onChange={(e) => setProfileField('zip', e.target.value)} className="mt-1" /></div>
              </div>
            </div>
            <div className="mt-6">
              <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ── Branding Tab ──────────────────────── */}
        {isISO && (
          <TabsContent value="branding">
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Branding Controls */}
              <Card className="p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">Brand Customization</h3>
                  <Button variant="outline" size="sm" onClick={resetBranding}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Defaults
                  </Button>
                </div>

                {/* Colors */}
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Colors</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Primary Color</Label>
                        <p className="text-xs text-muted-foreground mb-2">Buttons, active nav, links</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={branding.primary_color}
                            onChange={(e) => setBrandField('primary_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border cursor-pointer"
                            data-testid="brand-primary-color"
                          />
                          <Input value={branding.primary_color} onChange={(e) => setBrandField('primary_color', e.target.value)} className="font-mono max-w-[120px]" />
                        </div>
                      </div>
                      <div>
                        <Label>Accent Color</Label>
                        <p className="text-xs text-muted-foreground mb-2">Success states, highlights</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={branding.accent_color}
                            onChange={(e) => setBrandField('accent_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border cursor-pointer"
                            data-testid="brand-accent-color"
                          />
                          <Input value={branding.accent_color} onChange={(e) => setBrandField('accent_color', e.target.value)} className="font-mono max-w-[120px]" />
                        </div>
                      </div>
                      <div>
                        <Label>Sidebar Background</Label>
                        <p className="text-xs text-muted-foreground mb-2">Sidebar panel color</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={branding.sidebar_color}
                            onChange={(e) => setBrandField('sidebar_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border cursor-pointer"
                            data-testid="brand-sidebar-color"
                          />
                          <Input value={branding.sidebar_color} onChange={(e) => setBrandField('sidebar_color', e.target.value)} className="font-mono max-w-[120px]" />
                        </div>
                      </div>
                      <div>
                        <Label>Secondary Color</Label>
                        <p className="text-xs text-muted-foreground mb-2">Dark backgrounds, headings</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={branding.secondary_color}
                            onChange={(e) => setBrandField('secondary_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border cursor-pointer"
                            data-testid="brand-secondary-color"
                          />
                          <Input value={branding.secondary_color} onChange={(e) => setBrandField('secondary_color', e.target.value)} className="font-mono max-w-[120px]" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Logo */}
                  <div>
                    <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Logo & Favicon</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Logo URL</Label>
                        <p className="text-xs text-muted-foreground mb-2">Displayed in sidebar header</p>
                        <Input value={branding.logo_url} onChange={(e) => setBrandField('logo_url', e.target.value)} placeholder="https://..." data-testid="brand-logo-url" />
                      </div>
                      <div>
                        <Label>Favicon URL</Label>
                        <p className="text-xs text-muted-foreground mb-2">Browser tab icon</p>
                        <Input value={branding.favicon_url} onChange={(e) => setBrandField('favicon_url', e.target.value)} placeholder="https://..." data-testid="brand-favicon-url" />
                      </div>
                    </div>
                  </div>

                  {/* Font */}
                  <div>
                    <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Typography</p>
                    <div>
                      <Label>Font Family</Label>
                      <p className="text-xs text-muted-foreground mb-2">Applied across sidebar, proposals, and login page</p>
                      <select
                        value={branding.font}
                        onChange={(e) => setBrandField('font', e.target.value)}
                        className="w-full max-w-[260px] h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="brand-font-select"
                      >
                        <option value="Inter">Inter (Default)</option>
                        <option value="DM Sans">DM Sans</option>
                        <option value="Poppins">Poppins</option>
                        <option value="Montserrat">Montserrat</option>
                        <option value="Raleway">Raleway</option>
                        <option value="Source Sans Pro">Source Sans Pro</option>
                      </select>
                    </div>
                  </div>

                  {/* Lock/Unlock */}
                  <div>
                    <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Downline Permissions</p>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium">Allow Agents to Customize Branding</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          When enabled, agents and subagents can override the branding with their own colors and logo.
                          When disabled, your branding applies to all users in your organization.
                        </p>
                      </div>
                      <Switch
                        checked={branding.allow_downline_branding}
                        onCheckedChange={(v) => setBrandField('allow_downline_branding', v)}
                        data-testid="brand-allow-downline-toggle"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleSaveBranding} disabled={saving} data-testid="save-branding-btn">
                    {saving ? 'Saving...' : 'Save Branding'}
                  </Button>
                </div>
              </Card>

              {/* Live Preview */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold">Live Preview</h3>
                </div>
                <div className="border rounded-lg overflow-hidden shadow-sm">
                  {/* Preview Sidebar */}
                  <div className="flex" style={{ height: 280 }}>
                    <div className="w-[72px] flex flex-col border-r" style={{ backgroundColor: branding.sidebar_color }}>
                      {/* Preview Logo */}
                      <div className="p-2 border-b flex items-center justify-center h-12">
                        {branding.logo_url ? (
                          <img src={branding.logo_url} alt="Logo" className="h-7 w-7 object-contain" />
                        ) : (
                          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: branding.primary_color }}>
                            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4"><path d="M13 2L3 14h8l-2 8 10-12h-8l2-8z" fill="white" /></svg>
                          </div>
                        )}
                      </div>
                      {/* Preview Nav Items */}
                      <div className="p-1.5 space-y-1 flex-1">
                        <div className="h-6 rounded text-[8px] flex items-center px-1.5 text-white font-medium" style={{ backgroundColor: branding.primary_color }}>
                          Dash
                        </div>
                        {['Leads', 'Apps', 'Pay'].map((n) => (
                          <div key={n} className="h-6 rounded text-[8px] flex items-center px-1.5 text-muted-foreground hover:bg-accent/50">
                            {n}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Preview Content */}
                    <div className="flex-1 bg-background p-3">
                      <div className="h-8 border-b mb-3 flex items-center">
                        <div className="text-[10px] font-semibold" style={{ color: branding.secondary_color }}>Dashboard</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="h-14 rounded-md border p-2">
                          <div className="text-[8px] text-muted-foreground">Revenue</div>
                          <div className="text-[12px] font-bold">$12,450</div>
                        </div>
                        <div className="h-14 rounded-md border p-2">
                          <div className="text-[8px] text-muted-foreground">Leads</div>
                          <div className="text-[12px] font-bold">48</div>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button className="text-[9px] px-2 py-1 rounded text-white" style={{ backgroundColor: branding.primary_color }}>
                          New Lead
                        </button>
                        <button className="text-[9px] px-2 py-1 rounded text-white" style={{ backgroundColor: branding.accent_color }}>
                          Approved
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Login Page Preview */}
                <div className="mt-6">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Login Page Preview</p>
                  <div className="border rounded-lg p-4 bg-background shadow-sm text-center" style={{ fontFamily: branding.font }}>
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt="Logo" className="h-10 w-auto mx-auto mb-2 object-contain" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: branding.primary_color }}>
                        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6"><path d="M13 2L3 14h8l-2 8 10-12h-8l2-8z" fill="white" /></svg>
                      </div>
                    )}
                    <p className="text-sm font-bold" style={{ color: branding.secondary_color }}>{profile.company_name || 'Your Company'}</p>
                    <p className="text-[9px] text-muted-foreground mb-3">Sign in to your portal</p>
                    <div className="space-y-2 max-w-[140px] mx-auto">
                      <div className="h-5 rounded border bg-muted/30 text-[7px] flex items-center px-2 text-muted-foreground">email</div>
                      <div className="h-5 rounded border bg-muted/30 text-[7px] flex items-center px-2 text-muted-foreground">password</div>
                      <div className="h-5 rounded text-[8px] text-white font-medium flex items-center justify-center" style={{ backgroundColor: branding.primary_color }}>
                        Sign In
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subdomain Info */}
                {profile.subdomain && (
                  <div className="mt-4 p-3 border rounded-lg bg-muted/30">
                    <p className="text-xs font-semibold mb-1">Your Portal URL</p>
                    <p className="text-sm font-mono text-primary" data-testid="subdomain-url">
                      {profile.subdomain}.isocntrl.com
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Your agents and clients will see your branding at this URL
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        )}

        {/* ── Payment Account Tab ───────────────── */}
        <TabsContent value="payments">
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Merchant Account Status</h3>
              {!loadingMerchant && (
                <div className="flex items-center gap-2">
                  {statusIcon(merchantAccount?.status)}
                  <span className="text-sm font-medium">{statusLabel[merchantAccount?.status] || merchantAccount?.status}</span>
                </div>
              )}
            </div>
            {loadingMerchant ? (
              <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
            ) : merchantAccount?.status === 'active' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium">Your merchant account is active!</p>
                <p className="text-xs text-green-600 mt-1">Process payments via the Payments page in the sidebar.</p>
              </div>
            ) : merchantAccount?.status === 'pending' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium">Your application is under review</p>
                <p className="text-xs text-amber-600 mt-1">The Super Admin will activate your account once approved.</p>
              </div>
            ) : merchantAccount?.status === 'rejected' ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">Application was rejected</p>
                <p className="text-xs text-red-600 mt-1">Contact your administrator. You may reapply below.</p>
              </div>
            ) : null}
          </Card>

          {merchantAccount?.status !== 'active' && merchantAccount?.status !== 'pending' && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Apply for Merchant Account</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Complete the application below to enable payment processing for your organization.
              </p>
              <div ref={maverickRef} className="border rounded-lg p-4 bg-white min-h-[300px]" data-testid="maverick-boarding-container">
                <div id="maverick-boarding"></div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleApply} data-testid="submit-merchant-app-btn">
                  <Send className="h-4 w-4 mr-2" /> Submit Application
                </Button>
                <p className="text-xs text-muted-foreground">Complete the form above, then click Submit.</p>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
